import React, { useEffect, useMemo, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { adminAPI, raBillTemplatesAPI } from '../../services/authService';
import { downloadAttachment } from '../../utils/attachmentStorage';
import AttachmentDetailsModal from '../../components/AttachmentDetailsModal';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';
import ProjectNameText from '../../components/ProjectNameText';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';

const templateOptionLabel = (row) => {
  if (!row) return '';
  const status = row.status ? ` · ${row.status}` : '';
  return `${row.name || row.code} (${row.code} v${row.version}${status})`;
};

const Projects = () => {
  const [dataReady, setDataReady] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [useApiData, setUseApiData] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [projectForm, setProjectForm] = useState({
    name: '',
    agreement_prefix: '',
    letter_no: '',
    letter_date: '',
    status: 'Active',
    start_date: '',
    end_date: '',
    target_mandays: '',
    time_allowed_maintenance: '',
    time_allowed_dlp: '',
    location: '',
    project_type: '',
    contract_type: '',
    site_location_link: '',
    department_id: '',
    submittal_short_code: '',
    ra_bill_template_id: '',
  });
  const [briefDesignations, setBriefDesignations] = useState([]);
  const [scopeForm, setScopeForm] = useState({
    work_description: '',
    agreement_value: '',
    revised_value: '',
    percentage_under_clause_12: '',
    security_deposit_pct: 2.5,
    gst_pct: 2,
    income_tax_pct: 2,
    labour_cess_pct: 1,
    builtup_area_sqft: '',
    plot_area_acres: '',
    floor_count: '',
    basement_levels: '',
    key_structures: '',
    soil_type: '',
    fire_noc_status: '',
    environmental_clearance: '',
    scope_attachments: [],
  });
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadFileIndex, setUploadFileIndex] = useState(0);
  const [uploadTotalFiles, setUploadTotalFiles] = useState(0);
  const [viewingAttachment, setViewingAttachment] = useState(null);
  const [createWizardStep, setCreateWizardStep] = useState(1);
  const [customValues, setCustomValues] = useState({});
  const formConfig = useFormConfiguration({ pageKey: 'admin_projects', formKey: 'project_wizard', mode: editProject ? 'edit' : 'create' });
  const customFields = formConfig.customFields || [];

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };
  const dedupedDepartmentOptions = safeArray(departments).reduce((acc, dept) => {
    const key = String(dept?.code || dept?.name || '').trim().toUpperCase();
    if (!key) return acc;
    if (!acc.some((x) => String(x.code || x.name || '').trim().toUpperCase() === key)) {
      acc.push(dept);
    }
    return acc;
  }, []);
  const defaultDepartmentId = (dedupedDepartmentOptions.find((d) => String(d.code || '').toUpperCase() === 'CPWD')?.id ?? '');

  const templateById = useMemo(() => {
    const map = new Map();
    templates.forEach((row) => map.set(Number(row.id), row));
    return map;
  }, [templates]);

  const departmentDefaultTemplate = useMemo(() => {
    const dept = departments.find((d) => Number(d.id) === Number(projectForm.department_id));
    if (!dept) return null;
    const rawId = dept?.metadata?.ra_bill_template_id;
    if (rawId) return templateById.get(Number(rawId)) || null;
    return templates.find((t) => Number(t.departmentId) === Number(dept.id)) || null;
  }, [departments, projectForm.department_id, templateById, templates]);

  useEffect(() => {
    if (!showModal || editProject) return;
    if (!defaultDepartmentId) return;
    if (projectForm.department_id) return;
    setProjectForm((prev) => ({ ...prev, department_id: String(defaultDepartmentId) }));
  }, [showModal, editProject, defaultDepartmentId, projectForm.department_id]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const [projectsResponse, departmentsResponse, templatesResponse] = await Promise.all([
          adminAPI.getProjects(),
          adminAPI.getDepartments().catch(() => ({ data: [] })),
          raBillTemplatesAPI.list().catch(() => ({ data: [] })),
        ]);
        setUseApiData(true);
        const { rows } = unwrapListResponse(projectsResponse);
        const departmentRows = safeArray(unwrapListResponse(departmentsResponse).rows)
          .map((row) => ({
            id: row.id,
            name: row.name || '',
            code: row.code || '',
            status: row.status || 'Active',
            metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : {},
          }))
          .filter((row) => row.status === 'Active');
        const cpwdDefaultId = departmentRows.find((row) => String(row.code || '').toUpperCase() === 'CPWD')?.id || '';
        setDepartments(departmentRows);
        const tplRows = templatesResponse?.data?.data || templatesResponse?.data || [];
        setTemplates(safeArray(tplRows));
        const mapped = safeArray(rows).map((row) => {
          const meta = row.metadata || {};
          const scope = meta.scope || {};
          return {
            id: row.id,
            name: row.name,
            agreement_prefix: row.agreementPrefix || row.agreement_prefix || row.code || `PRJ-${row.id}`,
            letter_no: meta.letter_no ?? meta.letterOfAward?.letterNo ?? '',
            letter_date: meta.letter_date ?? meta.letterOfAward?.letterDate ?? '',
            percentage_under_clause_12: scope.percentage_under_clause_12 ?? '',
            status: row.status || 'Active',
            start_date: row.startDate ? String(row.startDate).slice(0, 10) : (row.start_date || ''),
            end_date: row.endDate ? String(row.endDate).slice(0, 10) : (row.end_date || ''),
            target_mandays: row.targetMandays ?? row.target_mandays ?? 0,
            time_allowed_maintenance: meta.time_allowed_maintenance ?? meta.timeAllowedMaintenance ?? '',
            time_allowed_dlp: meta.time_allowed_dlp ?? meta.timeAllowedDlp ?? meta.time_allowed_maintenance_dlp ?? meta.timeAllowedMaintenanceDlp ?? '',
            location: row.location ?? meta.location ?? '',
            project_type: meta.project_type ?? meta.projectType ?? '',
            contract_type: scope.contract_type ?? meta.contract_type ?? '',
            site_location_link: meta.site_location_link ?? meta.siteLocationLink ?? '',
            department_id: row.departmentId ?? (cpwdDefaultId || ''),
            department_name: row.department?.name || (cpwdDefaultId ? 'CPWD (Default)' : ''),
            brief_designations: meta.brief_designations || [],
            metadata: row.metadata || null,
          };
        });
        mapped.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setProjects(mapped);
      } catch (error) {
        console.warn('Projects API unavailable:', error?.message || error);
        setUseApiData(false);
        setProjects([]);
      } finally {
        setDataReady(true);
      }
    };
    loadProjects();
  }, []);

  const formatAttachmentSize = (sizeInBytes) => {
    if (!sizeInBytes) return '0 KB';
    const kb = sizeInBytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  const handleViewScopeAttachment = async (att) => {
    if (att.file) {
      const url = URL.createObjectURL(att.file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (att.data_url) {
      setViewingAttachment(att);
      return;
    }
    if (att.path || att.filename) {
      try {
        const projectId = editProject?.id;
        if (!projectId) return;
        const res = await adminAPI.fetchScopeAttachment(projectId, att.filename || att.path?.split(/[/\\]/).pop());
        const blob = res?.data;
        if (blob) {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      } catch (e) {
        console.warn('View failed:', e);
      }
    }
  };

  const handleDownloadScopeAttachment = async (att) => {
    if (att.file) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(att.file);
      a.download = att.name || 'attachment';
      a.click();
      URL.revokeObjectURL(a.href);
      return;
    }
    if (att.data_url) {
      const fallbackText = ['Project Scope Attachment', `File: ${att.name}`, ''].join('\n');
      downloadAttachment(att, fallbackText);
      return;
    }
    if (att.path || att.filename) {
      try {
        const projectId = editProject?.id;
        if (!projectId) return;
        const res = await adminAPI.fetchScopeAttachment(projectId, att.filename || att.path?.split(/[/\\]/).pop(), true);
        const blob = res?.data;
        if (blob) {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = att.name || 'attachment';
          a.click();
          URL.revokeObjectURL(a.href);
        }
      } catch (e) {
        console.warn('Download failed:', e);
      }
    }
  };

  const handleEdit = async (project) => {
    setEditProject(project);
    setCreateWizardStep(1);
    setFormErrors({});
    setAttachmentUploading(false);
    let scope = project?.metadata?.scope || {};
    let designations = Array.isArray(project?.metadata?.brief_designations) ? project.metadata.brief_designations : [];
    let fullMeta = project?.metadata || {};
    if (useApiData && project?.id) {
      try {
        const res = await adminAPI.getProjectById(project.id);
        const full = res?.data;
        if (full?.metadata) fullMeta = full.metadata;
        if (full?.metadata?.scope) scope = full.metadata.scope;
        if (Array.isArray(full?.metadata?.brief_designations)) designations = full.metadata.brief_designations;
      } catch (e) {
        console.warn('Could not fetch full project for edit:', e?.message || e);
      }
    }
    const projectTplId = fullMeta?.ra_bill_template_id;
    setProjectForm({
      name: project.name || '',
      agreement_prefix: project.agreement_prefix || '',
      letter_no: project.letter_no ?? '',
      letter_date: project.letter_date ?? '',
      status: project.status || 'Active',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      target_mandays: project.target_mandays ?? '',
      time_allowed_maintenance: project.time_allowed_maintenance ?? '',
      time_allowed_dlp: project.time_allowed_dlp ?? '',
      location: project.location ?? (fullMeta?.location ?? ''),
      project_type: project.project_type ?? (fullMeta?.project_type ?? ''),
      contract_type: project.contract_type ?? (fullMeta?.scope?.contract_type ?? fullMeta?.contract_type ?? ''),
      site_location_link: project.site_location_link ?? (fullMeta?.site_location_link ?? ''),
      department_id: project.department_id ? String(project.department_id) : '',
      submittal_short_code: fullMeta?.submittal_short_code ?? fullMeta?.submittalShortCode ?? '',
      ra_bill_template_id: projectTplId ? String(projectTplId) : '',
    });
    const rates = scope.ra_deduction_rates || project?.metadata?.ra_deduction_rates || project?.metadata?.scope?.ra_deduction_rates || {};
    setScopeForm({
      work_description: scope.work_description || '',
      agreement_value: scope.agreement_value ?? '',
      revised_value: scope.revised_value ?? '',
      percentage_under_clause_12: scope.percentage_under_clause_12 ?? '',
      security_deposit_pct: rates.security_deposit_pct ?? 2.5,
      gst_pct: rates.gst_pct ?? 2,
      income_tax_pct: rates.income_tax_pct ?? 2,
      labour_cess_pct: rates.labour_cess_pct ?? 1,
      builtup_area_sqft: scope.builtup_area_sqft ?? '',
      plot_area_acres: scope.plot_area_acres ?? '',
      floor_count: scope.floor_count ?? '',
      basement_levels: scope.basement_levels ?? '',
      key_structures: scope.key_structures || '',
      soil_type: scope.soil_type || '',
      fire_noc_status: scope.fire_noc_status || '',
      environmental_clearance: scope.environmental_clearance || '',
      scope_attachments: Array.isArray(scope.scope_attachments) ? scope.scope_attachments : [],
    });
    setBriefDesignations(designations.length ? designations : []);
    setCustomValues(defaultsFromCustomFields());
    if (formConfig.customFieldsEnabled && project?.id) {
      try {
        const res = await adminAPI.getCustomFieldValues('Project', project.id);
        setCustomValues({
          ...defaultsFromCustomFields(),
          ...(res?.data?.data?.values || res?.data?.values || {}),
        });
      } catch (_e) {
        /* keep defaults */
      }
    }
    setShowModal(true);
  };

  const handleOpenAddModal = () => {
    setEditProject(null);
    setFormErrors({});
    setAttachmentUploading(false);
    setProjectForm({
      name: '',
      agreement_prefix: '',
      letter_no: '',
      letter_date: '',
      status: 'Active',
      start_date: '',
      end_date: '',
      target_mandays: '',
      time_allowed_maintenance: '',
      time_allowed_dlp: '',
      location: '',
      project_type: '',
      contract_type: '',
      site_location_link: '',
      department_id: defaultDepartmentId ? String(defaultDepartmentId) : '',
      submittal_short_code: '',
      ra_bill_template_id: '',
    });
    setBriefDesignations([]);
    setScopeForm({
      work_description: '',
      agreement_value: '',
      revised_value: '',
      percentage_under_clause_12: '',
      security_deposit_pct: 2.5,
      gst_pct: 2,
      income_tax_pct: 2,
      labour_cess_pct: 1,
      builtup_area_sqft: '',
      plot_area_acres: '',
      floor_count: '',
      basement_levels: '',
      key_structures: '',
      soil_type: '',
      fire_noc_status: '',
      environmental_clearance: '',
      scope_attachments: [],
    });
    setCreateWizardStep(1);
    setCustomValues(defaultsFromCustomFields());
    setShowModal(true);
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const newAtts = files.map((f) => ({ file: f, name: f.name, size: f.size }));
    setScopeForm((prev) => ({ ...prev, scope_attachments: [...prev.scope_attachments, ...newAtts] }));
    event.target.value = '';
  };

  const handleRemoveScopeAttachment = (idx) => {
    setScopeForm((prev) => ({
      ...prev,
      scope_attachments: prev.scope_attachments.filter((_, i) => i !== idx),
    }));
  };

  const validate = () => {
    const errors = {};
    if (!projectForm.name.trim()) errors.name = 'Project name is required.';
    if (!projectForm.agreement_prefix.trim()) errors.agreement_prefix = 'Agreement No. is required.';
    if (!projectForm.start_date) errors.start_date = 'Start date is required.';
    if (!projectForm.end_date) errors.end_date = 'End date is required.';
    if (projectForm.start_date && projectForm.end_date && new Date(projectForm.end_date) < new Date(projectForm.start_date)) {
      errors.end_date = 'End date cannot be before start date.';
    }
    if (!editProject) {
      if (!scopeForm.work_description.trim()) errors.work_description = 'Work description is required.';
      if (!scopeForm.agreement_value || Number(scopeForm.agreement_value) <= 0) errors.agreement_value = 'Agreement Amount / Tendered value must be greater than zero.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCustomFields = () => {
    if (!formConfig.customFieldsEnabled) return true;
    for (const f of customFields) {
      if (!f.required || f.hidden) continue;
      const v = customValues[f.fieldKey];
      if (v === undefined || v === null || v === '') {
        notifyError(`${f.label || f.fieldKey} is required.`);
        return false;
      }
    }
    return true;
  };

  const validateCreateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!projectForm.name.trim()) errors.name = 'Project name is required.';
      if (!projectForm.agreement_prefix.trim()) errors.agreement_prefix = 'Agreement No. is required.';
      if (!projectForm.start_date) errors.start_date = 'Start date is required.';
      if (!projectForm.end_date) errors.end_date = 'End date is required.';
      if (projectForm.start_date && projectForm.end_date && new Date(projectForm.end_date) < new Date(projectForm.start_date)) {
        errors.end_date = 'End date cannot be before start date.';
      }
    }
    if (step === 2) {
      if (!scopeForm.work_description.trim()) errors.work_description = 'Work description is required.';
      if (!scopeForm.agreement_value || Number(scopeForm.agreement_value) <= 0) {
        errors.agreement_value = 'Agreement Amount / Tendered value must be greater than zero.';
      }
    }
    setFormErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const handleNextCreateStep = () => {
    if (!validateCreateStep(createWizardStep)) return;
    setCreateWizardStep((prev) => Math.min(3, prev + 1));
  };

  const handlePrevCreateStep = () => {
    setCreateWizardStep((prev) => Math.max(1, prev - 1));
  };

  const handleCreateProjectWithScope = async () => {
    if (!validate()) return;
    if (!validateCustomFields()) return;
    const isUpdate = !!editProject?.id;
    if (!(await confirmDialog(
      isUpdate ? 'Update this project and its scope?' : 'Create this project with scope?',
      isUpdate ? 'Update Project' : 'New Project'
    ))) return;

    if (useApiData) {
      const createViaApi = async () => {
        try {
          const scopePayload = {
            ...scopeForm,
            agreement_value: Number(scopeForm.agreement_value || 0),
            revised_value: Number(scopeForm.revised_value || 0),
            builtup_area_sqft: scopeForm.builtup_area_sqft ? Number(scopeForm.builtup_area_sqft) : null,
            plot_area_acres: scopeForm.plot_area_acres ? Number(scopeForm.plot_area_acres) : null,
            floor_count: scopeForm.floor_count ? Number(scopeForm.floor_count) : null,
            basement_levels: scopeForm.basement_levels ? Number(scopeForm.basement_levels) : null,
            ra_deduction_rates: {
              security_deposit_pct: scopeForm.security_deposit_pct === '' ? undefined : Number(scopeForm.security_deposit_pct),
              gst_pct: scopeForm.gst_pct === '' ? undefined : Number(scopeForm.gst_pct),
              income_tax_pct: scopeForm.income_tax_pct === '' ? undefined : Number(scopeForm.income_tax_pct),
              labour_cess_pct: scopeForm.labour_cess_pct === '' ? undefined : Number(scopeForm.labour_cess_pct),
            },
          };
          const sanitizeDiskAtt = (a) => (a && typeof a === 'object' && (a.path || a.filename)
            ? { name: a.name, path: a.path, filename: a.filename, size: a.size, type: a.type, uploaded_at: a.uploaded_at }
            : null);
          const sanitizeLegacyAtt = (a) => (a && typeof a === 'object' && (a.data_url || (!a.path && !a.filename && !a.file))
            ? { name: a.name, size: a.size, type: a.type, data_url: a.data_url }
            : null);

          if (editProject?.id) {
            const pendingFiles = scopeForm.scope_attachments.filter((a) => a?.file);
            setAttachmentUploading(true);
            setUploadTotalFiles(pendingFiles.length);
            try {
              const diskAtts = scopeForm.scope_attachments.filter((a) => a?.path || a?.filename).map(sanitizeDiskAtt).filter(Boolean);
              const legacyAtts = scopeForm.scope_attachments.filter((a) => !a?.file && !(a?.path || a?.filename)).map(sanitizeLegacyAtt).filter(Boolean);
              const keptExisting = [...diskAtts, ...legacyAtts];
              const uploadedMeta = [];
              for (let i = 0; i < pendingFiles.length; i++) {
                const att = pendingFiles[i];
                setUploadFileIndex(i + 1);
                setUploadFileName(att.file.name || 'file');
                setUploadProgress(0);
                const res = await adminAPI.uploadScopeAttachment(editProject.id, att.file, (pct) => setUploadProgress(pct));
                if (res?.data) uploadedMeta.push(res.data);
                setUploadProgress(100);
              }
              scopePayload.scope_attachments = [...keptExisting, ...uploadedMeta];
            } finally {
              setAttachmentUploading(false);
              setUploadProgress(0);
              setUploadFileName('');
              setUploadFileIndex(0);
              setUploadTotalFiles(0);
            }
            const payload = {
              name: projectForm.name.trim(),
              code: projectForm.agreement_prefix.trim(),
              agreementPrefix: projectForm.agreement_prefix.trim(),
              status: projectForm.status,
              startDate: projectForm.start_date ? new Date(projectForm.start_date).toISOString() : null,
              endDate: projectForm.end_date ? new Date(projectForm.end_date).toISOString() : null,
              targetMandays: Number(projectForm.target_mandays || 0),
              departmentId: projectForm.department_id ? Number(projectForm.department_id) : (defaultDepartmentId || null),
              location: projectForm.location?.trim() || null,
              metadata: {
                ...(editProject?.metadata || {}),
                letter_no: projectForm.letter_no || undefined,
                letter_date: projectForm.letter_date || undefined,
                letterOfAward: { letterNo: projectForm.letter_no || undefined, letterDate: projectForm.letter_date || undefined },
                time_allowed_maintenance: projectForm.time_allowed_maintenance || undefined,
                timeAllowedMaintenance: projectForm.time_allowed_maintenance || undefined,
                time_allowed_dlp: projectForm.time_allowed_dlp || undefined,
                timeAllowedDlp: projectForm.time_allowed_dlp || undefined,
                project_type: projectForm.project_type?.trim() || undefined,
                contract_type: projectForm.contract_type?.trim() || undefined,
                site_location_link: projectForm.site_location_link?.trim() || undefined,
                brief_designations: briefDesignations.filter((d) => d && String(d).trim()),
                submittal_short_code: projectForm.submittal_short_code?.trim().toUpperCase() || undefined,
                ra_bill_template_id: projectForm.ra_bill_template_id
                  ? Number(projectForm.ra_bill_template_id)
                  : null,
                scope: { ...scopePayload, contract_type: projectForm.contract_type?.trim() || undefined },
              },
            };
            const response = await adminAPI.updateProject(editProject.id, payload);
            const updated = response?.data;
            setProjects((prev) => prev.map((project) => {
              if (String(project.id) !== String(editProject.id)) return project;
              const meta = updated.metadata || {};
              const scope = meta.scope || {};
              return {
                id: updated.id,
                name: updated.name,
                agreement_prefix: updated.agreementPrefix || updated.agreement_prefix || updated.code || `PRJ-${updated.id}`,
                status: updated.status || 'Active',
                start_date: updated.startDate ? String(updated.startDate).slice(0, 10) : (updated.start_date || ''),
                end_date: updated.endDate ? String(updated.endDate).slice(0, 10) : (updated.end_date || ''),
                target_mandays: updated.targetMandays ?? updated.target_mandays ?? 0,
                letter_no: meta.letter_no ?? meta.letterOfAward?.letterNo ?? '',
                letter_date: meta.letter_date ?? meta.letterOfAward?.letterDate ?? '',
                percentage_under_clause_12: scope.percentage_under_clause_12 ?? '',
                time_allowed_maintenance: meta.time_allowed_maintenance ?? meta.timeAllowedMaintenance ?? '',
                time_allowed_dlp: meta.time_allowed_dlp ?? meta.timeAllowedDlp ?? '',
                location: updated.location ?? meta.location ?? '',
                project_type: meta.project_type ?? meta.projectType ?? '',
                site_location_link: meta.site_location_link ?? meta.siteLocationLink ?? '',
                department_id: updated.departmentId ?? '',
                department_name: updated.department?.name || '',
                brief_designations: meta.brief_designations || [],
                metadata: updated.metadata || null,
              };
            }));
            if (formConfig.customFieldsEnabled) {
              try {
                await adminAPI.saveCustomFieldValues('Project', editProject.id, {
                  pageKey: 'admin_projects',
                  projectId: editProject.id,
                  values: customValues,
                  mode: 'edit',
                });
              } catch (cvErr) {
                console.error('Custom field save failed:', cvErr);
                notifyError(cvErr?.response?.data?.message || 'Project saved but custom fields failed.');
              }
            }
            notifySuccess('Project updated successfully.');
          } else {
            scopePayload.scope_attachments = [];
            const payload = {
              name: projectForm.name.trim(),
              code: projectForm.agreement_prefix.trim(),
              agreementPrefix: projectForm.agreement_prefix.trim(),
              status: projectForm.status,
              startDate: projectForm.start_date ? new Date(projectForm.start_date).toISOString() : null,
              endDate: projectForm.end_date ? new Date(projectForm.end_date).toISOString() : null,
              targetMandays: Number(projectForm.target_mandays || 0),
              departmentId: projectForm.department_id ? Number(projectForm.department_id) : (defaultDepartmentId || null),
              location: projectForm.location?.trim() || null,
              metadata: {
                letter_no: projectForm.letter_no || undefined,
                letter_date: projectForm.letter_date || undefined,
                letterOfAward: { letterNo: projectForm.letter_no || undefined, letterDate: projectForm.letter_date || undefined },
                time_allowed_maintenance: projectForm.time_allowed_maintenance || undefined,
                timeAllowedMaintenance: projectForm.time_allowed_maintenance || undefined,
                time_allowed_dlp: projectForm.time_allowed_dlp || undefined,
                timeAllowedDlp: projectForm.time_allowed_dlp || undefined,
                project_type: projectForm.project_type?.trim() || undefined,
                contract_type: projectForm.contract_type?.trim() || undefined,
                site_location_link: projectForm.site_location_link?.trim() || undefined,
                brief_designations: briefDesignations.filter((d) => d && String(d).trim()),
                submittal_short_code: projectForm.submittal_short_code?.trim().toUpperCase() || undefined,
                ra_bill_template_id: projectForm.ra_bill_template_id
                  ? Number(projectForm.ra_bill_template_id)
                  : null,
                scope: { ...scopePayload, contract_type: projectForm.contract_type?.trim() || undefined },
              },
            };
            const response = await adminAPI.createProject(payload);
            const created = response?.data;
            const pendingFiles = scopeForm.scope_attachments.filter((a) => a?.file);
            if (pendingFiles.length > 0) {
              setAttachmentUploading(true);
              setUploadTotalFiles(pendingFiles.length);
              try {
                for (let i = 0; i < pendingFiles.length; i++) {
                  const att = pendingFiles[i];
                  setUploadFileIndex(i + 1);
                  setUploadFileName(att.file.name || 'file');
                  setUploadProgress(0);
                  await adminAPI.uploadScopeAttachment(created.id, att.file, (pct) => setUploadProgress(pct));
                  setUploadProgress(100);
                }
              } finally {
                setAttachmentUploading(false);
                setUploadProgress(0);
                setUploadFileName('');
                setUploadFileIndex(0);
                setUploadTotalFiles(0);
              }
            }
            const meta = created.metadata || {};
            const scope = meta.scope || {};
            const newProject = {
              id: created.id,
              name: created.name,
              agreement_prefix: created.agreementPrefix || created.agreement_prefix || created.code || `PRJ-${created.id}`,
              status: created.status || 'Active',
              start_date: created.startDate ? String(created.startDate).slice(0, 10) : (created.start_date || ''),
              end_date: created.endDate ? String(created.endDate).slice(0, 10) : (created.end_date || ''),
              target_mandays: created.targetMandays ?? created.target_mandays ?? 0,
              letter_no: meta.letter_no ?? meta.letterOfAward?.letterNo ?? '',
              letter_date: meta.letter_date ?? meta.letterOfAward?.letterDate ?? '',
              percentage_under_clause_12: scope.percentage_under_clause_12 ?? scopeForm.percentage_under_clause_12 ?? '',
              time_allowed_maintenance: meta.time_allowed_maintenance ?? meta.timeAllowedMaintenance ?? '',
              time_allowed_dlp: meta.time_allowed_dlp ?? meta.timeAllowedDlp ?? '',
              location: created.location ?? meta.location ?? '',
              project_type: meta.project_type ?? meta.projectType ?? '',
              contract_type: scope.contract_type ?? meta.contract_type ?? '',
              site_location_link: meta.site_location_link ?? meta.siteLocationLink ?? '',
              department_id: created.departmentId ?? '',
              department_name: created.department?.name || '',
              brief_designations: meta.brief_designations || [],
              metadata: created.metadata || null,
            };
            setProjects((prev) => {
              const next = [newProject, ...prev];
              next.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
              return next;
            });
            if (formConfig.customFieldsEnabled && created?.id) {
              try {
                await adminAPI.saveCustomFieldValues('Project', created.id, {
                  pageKey: 'admin_projects',
                  projectId: created.id,
                  values: customValues,
                  mode: 'create',
                });
              } catch (cvErr) {
                console.error('Custom field save failed:', cvErr);
                notifyError(cvErr?.response?.data?.message || 'Project saved but custom fields failed.');
              }
            }
            notifySuccess('Project created successfully.');
          }
          setShowModal(false);
        } catch (error) {
          console.error('Project save failed via API:', error);
          const status = error?.response?.status;
          const msg = error?.response?.data?.message || error?.message || 'Please retry.';
          if (status === 409) {
            notifyError(msg);
          } else {
            notifyError(`Unable to save project: ${msg}`);
          }
        }
      };
      createViaApi();
      return;
    }

    notifyError('API required to create projects. Please ensure the API is available.');
  };

  const handleRemoveProject = async () => {
    if (!editProject?.id) return;
    if (!(await confirmDialog(
      `Remove project "${editProject.name}"? This action cannot be undone.`,
      'Remove Project'
    ))) return;

    if (useApiData) {
      try {
        await adminAPI.deleteProject(editProject.id);
        setProjects((prev) => prev.filter((p) => String(p.id) !== String(editProject.id)));
        setShowModal(false);
        setEditProject(null);
        notifySuccess('Project removed successfully.');
      } catch (error) {
        console.error('Delete project failed via API:', error);
        notifyError('Unable to remove project. Please retry.');
      }
    } else {
      setProjects((prev) => prev.filter((p) => String(p.id) !== String(editProject.id)));
      setShowModal(false);
      setEditProject(null);
      notifySuccess('Project removed.');
    }
  };

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>List of Projects</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>List of Projects <span className="count-badge">{projects.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" onClick={handleOpenAddModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create Project
          </button>
        </div>
      </div>

      <div className="cards-grid">
        {safeArray(projects).map((project) => (
          <div key={project.id} className="card" onClick={() => handleEdit(project)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                <ProjectNameText as="span">{project.name}</ProjectNameText>
              </h3>
              <span className={`status-badge ${project.status === 'Active' ? 'active' : 'closed'}`}>{project.status}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#6b7280' }}>Agreement No.</span>
                <span style={{ fontWeight: 500, color: '#7C3AED' }}>{project.agreement_prefix}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#6b7280' }}>Department</span>
                <span style={{ fontWeight: 500, color: '#374151' }}>{project.department_name || 'Not mapped'}</span>
              </div>
              {(project.percentage_under_clause_12 != null && project.percentage_under_clause_12 !== '') && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#6b7280' }}>Percentage Under Clause 12</span>
                  <span style={{ fontWeight: 500, color: '#374151' }}>{project.percentage_under_clause_12}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#6b7280' }}>Duration</span>
                <span style={{ color: '#374151' }}>{formatDateIN(project.start_date)} to {formatDateIN(project.end_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#6b7280' }}>Time allowed for Construction</span>
                <span style={{ fontWeight: 500, color: '#374151' }}>{project.target_mandays != null && project.target_mandays !== '' ? Number(project.target_mandays).toLocaleString() : '-'}</span>
              </div>
              {((project.time_allowed_maintenance != null && project.time_allowed_maintenance !== '') || (project.time_allowed_dlp != null && project.time_allowed_dlp !== '')) && (
                <>
                  {project.time_allowed_maintenance != null && project.time_allowed_maintenance !== '' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#6b7280' }}>Time allowed for Maintenance</span>
                      <span style={{ fontWeight: 500, color: '#374151' }}>{project.time_allowed_maintenance}</span>
                    </div>
                  )}
                  {project.time_allowed_dlp != null && project.time_allowed_dlp !== '' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#6b7280' }}>Defect Liability Period</span>
                      <span style={{ fontWeight: 500, color: '#374151' }}>{project.time_allowed_dlp}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div
            className="modal-content project-wizard-modal"
            style={{
              width: '100vw',
              height: '100vh',
              maxWidth: '100vw',
              maxHeight: '100vh',
              margin: 0,
              borderRadius: 0,
            }}
          >
            <div className="modal-header">
              <h2>{editProject ? 'Edit Project' : 'Create Project'}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', paddingTop: 10 }}>
              <style>{`
                .project-wizard-modal .form-row {
                  display: grid;
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                  gap: 12px 16px;
                  align-items: start;
                }
                @media (max-width: 720px) {
                  .project-wizard-modal .form-row { grid-template-columns: 1fr; }
                }
                .project-wizard-modal .form-row.form-row-1col { grid-template-columns: 1fr; }
                .project-wizard-modal .form-group.form-col-span-full { grid-column: 1 / -1; }
                .project-wizard-modal .form-group input,
                .project-wizard-modal .form-group select,
                .project-wizard-modal .form-group textarea { width: 100%; }
              `}</style>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { id: 1, label: 'Step 1: Basic Details' },
                  { id: 2, label: 'Step 2: Scope Setup' },
                  { id: 3, label: 'Step 3: Technical & Attachments' },
                ].map((step) => (
                  <button
                    key={step.id}
                    type="button"
                    className={createWizardStep === step.id ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                    onClick={() => setCreateWizardStep(step.id)}
                  >
                    {step.label}
                  </button>
                ))}
              </div>
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              {createWizardStep === 1 && (
              <>
              <div className="form-row">
                <ConfiguredField fieldKey="name" formConfig={formConfig} defaultLabel="Project Name" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <>
                      <input type="text" value={projectForm.name} disabled={readOnly} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} placeholder={placeholder || 'Enter project name'} />
                      {formErrors.name && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.name}</p>}
                    </>
                  )}
                </ConfiguredField>
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" value={projectForm.location} onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })} placeholder="e.g. Greater Noida West, UP" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Project Department</label>
                  <select value={projectForm.department_id} onChange={(e) => setProjectForm({ ...projectForm, department_id: e.target.value })}>
                    <option value="">Select Department</option>
                    {dedupedDepartmentOptions.map((d) => {
                      const name = String(d.name || '').trim();
                      const code = String(d.code || '').trim();
                      const label = name && code && name.toUpperCase() !== code.toUpperCase()
                        ? `${name} (${code})`
                        : (name || code);
                      return <option key={d.id} value={d.id}>{label}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type of Contract</label>
                  <select value={projectForm.contract_type} onChange={(e) => setProjectForm({ ...projectForm, contract_type: e.target.value })}>
                    <option value="">Select</option>
                    <option value="EPC">EPC</option>
                    <option value="Item rate % Contract">Item rate % Contract</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 420px' }}>
                  <label>RA Bill Template</label>
                  <select
                    value={projectForm.ra_bill_template_id}
                    onChange={(e) => setProjectForm({ ...projectForm, ra_bill_template_id: e.target.value })}
                  >
                    <option value="">
                      {departmentDefaultTemplate
                        ? `Department default (${departmentDefaultTemplate.name})`
                        : 'None (use department default)'}
                    </option>
                    {templates.map((row) => (
                      <option key={row.id} value={String(row.id)}>{templateOptionLabel(row)}</option>
                    ))}
                  </select>
                  <p style={{ fontSize: 11, color: '#64748b', marginTop: 6, marginBottom: 0 }}>
                    {projectForm.ra_bill_template_id
                      ? 'Project override — new RA bills use this template.'
                      : 'Inherits the department template when empty. Existing bills stay frozen.'}
                  </p>
                </div>
              </div>
              <div style={{ margin: '16px 0 8px', paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: 14, color: '#1f2937' }}>Letter of Award / acceptance details</h3>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Submittal Short Code</label>
                  <input type="text" maxLength={20} value={projectForm.submittal_short_code} onChange={(e) => setProjectForm({ ...projectForm, submittal_short_code: e.target.value.toUpperCase() })} placeholder="e.g. NDRS (optional)" />
                </div>
                <ConfiguredField fieldKey="agreement_prefix" formConfig={formConfig} defaultLabel="Agreement No." defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <>
                      <input type="text" value={projectForm.agreement_prefix} disabled={readOnly} onChange={(e) => setProjectForm({ ...projectForm, agreement_prefix: e.target.value })} placeholder={placeholder || 'e.g. NH48/PH2'} />
                      <p style={{ fontSize: 11, color: '#64748b', marginTop: 6, marginBottom: 0 }}>Must be unique across all projects (stored as project code).</p>
                      {formErrors.agreement_prefix && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.agreement_prefix}</p>}
                    </>
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="agreement_value" formConfig={formConfig} defaultLabel="Agreement Amount / Tendered value (₹)" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <>
                      <input type="number" min="0" value={scopeForm.agreement_value} disabled={readOnly} onChange={(e) => setScopeForm({ ...scopeForm, agreement_value: Math.max(0, Number(e.target.value) || 0) })} placeholder={placeholder || '0'} />
                      {formErrors.agreement_value && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.agreement_value}</p>}
                    </>
                  )}
                </ConfiguredField>
                <div className="form-group">
                  <label>Letter No.</label>
                  <input type="text" value={projectForm.letter_no} onChange={(e) => setProjectForm({ ...projectForm, letter_no: e.target.value })} placeholder="Letter reference number" />
                </div>
                <div className="form-group">
                  <label>Letter date</label>
                  <input type="date" value={projectForm.letter_date} onChange={(e) => setProjectForm({ ...projectForm, letter_date: e.target.value })} />
                </div>
              </div>
              <div className="form-row form-row-1col">
                <ConfiguredField fieldKey="status" formConfig={formConfig} defaultLabel="Status">
                  {({ readOnly }) => (
                    <select value={projectForm.status} disabled={readOnly} onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}>
                      <option value="Active">Active</option>
                      <option value="Closed">Closed</option>
                      <option value="WIP">WIP - Work in Progress</option>
                      <option value="WC-FBNS">WC-FBNS - Work completed - Final bill not Submitted</option>
                      <option value="WC-FBS">WC-FBS - Work completed - Final bill Submitted</option>
                      <option value="WC-FBR">WC-FBR - Work completed - Final bill received</option>
                      <option value="WC-BGs">WC-BGs - Work completed – Bank Guarantees not received</option>
                    </select>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="start_date" formConfig={formConfig} defaultLabel="Start Date" defaultRequired>
                  {({ readOnly }) => (
                    <>
                      <input type="date" value={projectForm.start_date} disabled={readOnly} onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })} />
                      {formErrors.start_date && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.start_date}</p>}
                    </>
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="end_date" formConfig={formConfig} defaultLabel="End Date" defaultRequired>
                  {({ readOnly }) => (
                    <>
                      <input type="date" value={projectForm.end_date} disabled={readOnly} onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })} />
                      {formErrors.end_date && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.end_date}</p>}
                    </>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Time allowed for Construction</label>
                  <input type="number" min="0" value={projectForm.target_mandays} onChange={(e) => setProjectForm({ ...projectForm, target_mandays: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. man-days" />
                </div>
                <div className="form-group">
                  <label>Time allowed for Maintenance</label>
                  <input type="number" min="0" value={projectForm.time_allowed_maintenance} onChange={(e) => setProjectForm({ ...projectForm, time_allowed_maintenance: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. days" />
                </div>
                <div className="form-group">
                  <label>Defect Liability Period</label>
                  <input type="number" min="0" value={projectForm.time_allowed_dlp} onChange={(e) => setProjectForm({ ...projectForm, time_allowed_dlp: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. days" />
                </div>
              </div>
              </>
              )}

              {createWizardStep === 2 && (
              <>
              <div style={{ margin: '16px 0 8px', paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: 14, color: '#1f2937' }}>Brief Designations</h3>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Designations for From/To dropdowns in correspondence (Inward: From, Outward: To)</p>
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  {briefDesignations.map((d, idx) => (
                    <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F5F3FF', border: '1px solid #dbeafe', borderRadius: 6, padding: '6px 10px' }}>
                      <input
                        type="text"
                        value={d}
                        onChange={(e) => setBriefDesignations((prev) => prev.map((x, i) => (i === idx ? e.target.value : x)))}
                        placeholder="e.g. NHAI PIU"
                        style={{ width: 140, border: 'none', background: 'transparent', fontSize: 13 }}
                      />
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setBriefDesignations((prev) => prev.filter((_, i) => i !== idx))} title="Remove">✕</button>
                    </span>
                  ))}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setBriefDesignations((prev) => [...prev, ''])}>+ Add Designation</button>
                </div>
              </div>

              <div style={{ margin: '16px 0 8px', paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: 14, color: '#1f2937' }}>Project Scope Details</h3>
              </div>

              <ConfiguredField fieldKey="work_description" formConfig={formConfig} defaultLabel="Work Description" defaultRequired>
                {({ readOnly, placeholder }) => (
                  <>
                    <textarea rows={3} value={scopeForm.work_description} disabled={readOnly} onChange={(e) => setScopeForm({ ...scopeForm, work_description: e.target.value })} placeholder={placeholder || 'Detailed project scope...'} />
                    {formErrors.work_description && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.work_description}</p>}
                  </>
                )}
              </ConfiguredField>
              <div className="form-row">
                <div className="form-group">
                  <label>Percentage Under Clause 12</label>
                  <input type="text" value={scopeForm.percentage_under_clause_12} onChange={(e) => setScopeForm({ ...scopeForm, percentage_under_clause_12: e.target.value })} placeholder="e.g. 2.5%" />
                </div>
                <div className="form-group">
                  <label>Revised value (₹)</label>
                  <input type="number" min="0" value={scopeForm.revised_value} onChange={(e) => setScopeForm({ ...scopeForm, revised_value: Math.max(0, Number(e.target.value) || 0) })} placeholder="0" />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>RA Recovery / Deduction % (Master data for RA Bills)</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Security Deposit % (default 2.5)</label>
                    <input type="number" min="0" step="0.01" value={scopeForm.security_deposit_pct} onChange={(e) => setScopeForm({ ...scopeForm, security_deposit_pct: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="2.5" />
                  </div>
                  <div className="form-group">
                    <label>GST % (default 2)</label>
                    <input type="number" min="0" step="0.01" value={scopeForm.gst_pct} onChange={(e) => setScopeForm({ ...scopeForm, gst_pct: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="2" />
                  </div>
                  <div className="form-group">
                    <label>Income Tax % (default 2)</label>
                    <input type="number" min="0" step="0.01" value={scopeForm.income_tax_pct} onChange={(e) => setScopeForm({ ...scopeForm, income_tax_pct: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="2" />
                  </div>
                  <div className="form-group">
                    <label>Labour Cess % (default 1)</label>
                    <input type="number" min="0" step="0.01" value={scopeForm.labour_cess_pct} onChange={(e) => setScopeForm({ ...scopeForm, labour_cess_pct: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="1" />
                  </div>
                </div>
              </div>
              </>
              )}

              {createWizardStep === 3 && (
              <>
              <div className="form-row">
                <div className="form-group">
                  <label>Built-up Area (sq ft, optional)</label>
                  <input type="number" min="0" value={scopeForm.builtup_area_sqft} onChange={(e) => setScopeForm({ ...scopeForm, builtup_area_sqft: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. 125000" />
                </div>
                <div className="form-group">
                  <label>Plot Area (acres, optional)</label>
                  <input type="number" min="0" step="0.01" value={scopeForm.plot_area_acres} onChange={(e) => setScopeForm({ ...scopeForm, plot_area_acres: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. 10" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Number of Floors (optional)</label>
                  <input type="number" min="0" value={scopeForm.floor_count} onChange={(e) => setScopeForm({ ...scopeForm, floor_count: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. 8" />
                </div>
                <div className="form-group">
                  <label>Basement Levels (optional)</label>
                  <input type="number" min="0" value={scopeForm.basement_levels} onChange={(e) => setScopeForm({ ...scopeForm, basement_levels: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })} placeholder="e.g. 2" />
                </div>
              </div>
              <div className="form-group">
                <label>Key Structures</label>
                <input type="text" value={scopeForm.key_structures} onChange={(e) => setScopeForm({ ...scopeForm, key_structures: e.target.value })} placeholder="Flyovers, underpasses, station blocks..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Soil Type</label>
                  <input type="text" value={scopeForm.soil_type} onChange={(e) => setScopeForm({ ...scopeForm, soil_type: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fire NOC Status</label>
                  <input type="text" value={scopeForm.fire_noc_status} onChange={(e) => setScopeForm({ ...scopeForm, fire_noc_status: e.target.value })} placeholder="Approved / Pending" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-col-span-full">
                  <label>Environmental Clearance</label>
                  <input type="text" value={scopeForm.environmental_clearance} onChange={(e) => setScopeForm({ ...scopeForm, environmental_clearance: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Site Location Link</label>
                <input type="url" value={projectForm.site_location_link} onChange={(e) => setProjectForm({ ...projectForm, site_location_link: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}
              <div className="form-group">
                <label>Scope / Agreement Attachments</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={handleAttachmentChange}
                  disabled={attachmentUploading}
                />
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  Upload scope/agreement files (max 1 GB per file). Files are stored on the server.
                </p>
                {attachmentUploading && (
                  <div style={{ marginTop: 12, padding: 12, background: '#F5F3FF', borderRadius: 8, border: '1px solid #93c5fd' }}>
                    <p style={{ fontSize: 13, color: '#6D28D9', margin: '0 0 8px 0', fontWeight: 500 }}>
                      {uploadTotalFiles > 1
                        ? `Uploading file ${uploadFileIndex} of ${uploadTotalFiles}: ${uploadFileName}`
                        : `Uploading: ${uploadFileName}`}
                    </p>
                    <div style={{ height: 8, background: '#EDE9FE', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${uploadProgress}%`, height: '100%', background: '#7C3AED', borderRadius: 4, transition: 'width 0.2s' }} />
                    </div>
                    <p style={{ fontSize: 12, color: '#3b82f6', margin: '6px 0 0 0' }}>{uploadProgress}%</p>
                  </div>
                )}
                {scopeForm.scope_attachments.length > 0 && !attachmentUploading && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {scopeForm.scope_attachments.map((file, idx) => (
                      <div key={`${file.name}-${file.size}-${idx}`} style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span>{file.name} ({formatAttachmentSize(file.size)})</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#16a34a', fontSize: 14 }} title="Upload complete">✓</span>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleViewScopeAttachment(file)}>View</button>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleDownloadScopeAttachment(file)}>Download</button>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveScopeAttachment(idx)} title="Remove">✕</button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </>
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                {editProject && (
                  <button className="btn-secondary" onClick={handleRemoveProject} style={{ color: '#dc2626', borderColor: '#dc2626' }}>
                    Remove
                  </button>
                )}
                {createWizardStep > 1 && (
                  <button className="btn-secondary" onClick={handlePrevCreateStep}>Back</button>
                )}
                {createWizardStep < 3 && (
                  <button className="btn-primary" onClick={handleNextCreateStep}>Next</button>
                )}
                {createWizardStep === 3 && (
                  <button className="btn-primary" onClick={handleCreateProjectWithScope} disabled={attachmentUploading}>
                    {editProject ? 'Update' : 'Create'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingAttachment && (
        <AttachmentDetailsModal
          file={viewingAttachment}
          onClose={() => setViewingAttachment(null)}
          onDownload={(f) => { handleDownloadScopeAttachment(f); setViewingAttachment(null); }}
          extraMetadata={[{ label: 'Project', value: editProject?.name || projectForm?.name || '-' }]}
        />
      )}
    </div>
  );
};

export default Projects;
